// help/P2PHelp.js

export default function P2PHelp() {
  return (
    <div className="space-y-8">

      {/* =======================================================
          HEADER
      ======================================================== */}

      <div>
        <h2 className="text-2xl font-bold">
          P2P Marketplace
        </h2>

        <p className="mt-2 text-sm text-neutral-600 dark:text-neutral-400">
          The Metisel P2P Marketplace allows users to buy and sell
          USWD and USDT directly with one another, and lets buyers
          settle USDT purchases in USWD or GHSX. The platform securely
          manages orders, payment confirmation and asset release while
          providing a transparent trading experience.
        </p>
      </div>

{/* =======================================================
    PAYOUT CURRENCY
======================================================== */}

<section>
  <h3 className="text-lg font-semibold">
    Setting Your Payout Currency
  </h3>

  <ul className="mt-3 list-disc pl-6 space-y-2 text-sm">
    <li>
      The first time you switch to <strong>Sell</strong> — for either
      USWD or USDT — you'll be asked to choose the currency buyers
      will pay you in.
    </li>

    <li>
      Select your currency and confirm to continue.
    </li>

    <li>
      You can only have one payout currency at a time — offers in
      the marketplace are filtered by currency.
    </li>
  </ul>
</section>

{/* =======================================================
    CREATING AN AD
======================================================== */}

<section>
  <h3 className="text-lg font-semibold">
    Creating an Advertisement
  </h3>

  <ul className="mt-3 list-disc pl-6 space-y-2 text-sm">
    <li>
      Open the <strong>P2P</strong> section and switch to the
      <strong> Sell</strong> tab.
    </li>

    <li>
      Choose the asset you want to sell — <strong>USWD</strong> or
      <strong> USDT</strong>.
    </li>

    <li>
      Enter your selling rate, minimum/maximum order limits, and the
      amount available for sale.
    </li>

    <li>
      If required, save your preferred payment methods and any
      payment instructions before publishing your advertisement.
    </li>

    <li>
      Submit the advertisement to make it visible in the marketplace.
    </li>
  </ul>

  <p className="mt-3 text-neutral-600 dark:text-neutral-400">
    <strong>Selling USWD</strong> is instant — the amount is checked
    against your available USWD balance and the ad goes live right
    away.
  </p>

 <p className="mt-3 text-neutral-600 dark:text-neutral-400">
  <strong>Selling USDT</strong> involves an on-chain step. The first time you list a
  USDT ad, a refundable deposit of <strong>0.5 USWD</strong> is set
  aside automatically to cover the on-chain rent and network fees
  for creating your USDT wallet accounts.This deposit is returned
  to you when those accounts are closed as part of deleting your
  account.
</p>
</section>

{/* =======================================================
    MANAGING ADS
======================================================== */}

<section>
  <h3 className="text-lg font-semibold">
    Managing Your Advertisements
  </h3>

  <ul className="mt-3 list-disc pl-6 space-y-2 text-sm">
    <li>
      Your own advertisements show <strong>Cancel</strong> and
      <strong> Edit</strong> buttons instead of a Buy button in the
      marketplace list.
    </li>

    <li>
      Tap <strong>Edit</strong> to update your rate, limits, or
      available amount — the sell form is pre-filled with your
      current ad.
    </li>

    <li>
      Tap <strong>Cancel</strong> and confirm to remove an
      advertisement.
    </li>
  </ul>

  <p className="mt-3 text-neutral-600 dark:text-neutral-400">
    Cancelling a <strong>USWD</strong> ad is instant and removes it
    from the marketplace immediately, as long as it has no active
    orders.
  </p>

  <p className="mt-3 text-neutral-600 dark:text-neutral-400">
    Cancelling a <strong>USDT</strong> ad triggers an on-chain
    transaction that returns your remaining USDT to your wallet,Editing a
    USDT ad's amount also moves funds on-chain: increasing it
    requires you to sign another transaction, while decreasing it is
    refunded automatically.
  </p>
</section>
      {/* =======================================================
          BUYING
      ======================================================== */}

      <section>
        <h3 className="text-lg font-semibold">
          Buying an Asset
        </h3>

        <ul className="mt-3 list-disc pl-6 space-y-2 text-sm">
          <li>
            Browse available advertisements in the marketplace.
          </li>

          <li>
            Use search and currency filters to find suitable offers.
          </li>

          <li>
            Select an advertisement and tap <strong>Buy</strong>.
          </li>

          <li>
            Enter the amount you want to purchase within the seller's
            trading limits.
          </li>

          <li>
            Choose a payment method from the seller's available options.
          </li>

          <li>
            Review the order summary and payment information before
            confirming.
          </li>

          <li>
            Once the order is created, you'll be taken to the payment
            screen and the seller's payment details will also be sent
            to you in chat.
          </li>
        </ul>

        <p className="mt-3 text-neutral-600 dark:text-neutral-400">
          Buying <strong>USDT</strong> settles instantly and works
          differently — instead of choosing a payment method, you
          choose a <strong>Settlement Asset</strong>, either
          <strong> USWD</strong> or <strong> GHSX</strong>, from your
          Metisel wallet. That asset is deducted (along with a small
          network fee — 0.01 USWD or 0.5 GHSX, depending on which you
          pick) and USDT is credited to you right away, so there's no
          payment method to choose or proof to upload.
        </p>
      </section>

      {/* =======================================================
          COMPLETING ORDER
      ======================================================== */}

      <section>
        <h3 className="text-lg font-semibold">
          Completing an Order
        </h3>

        <ul className="mt-3 list-disc pl-6 space-y-2 text-sm">
          <li>
            Send payment using the seller's preferred payment method,
            shown on the payment screen and in your chat with the
            seller.
          </li>

          <li>
            Return to the payment screen and tap the
            <strong> Proof</strong> button.
          </li>

          <li>
            Confirm the checkbox that you paid from an account you own —
            never pay from a third-party account, as this can result in
            suspension.
          </li>

          <li>
            Attach up to 3 images as payment proof.
          </li>

          <li>
            Tap <strong>"I have paid — Notify Seller"</strong> to submit.
          </li>

          <li>
            Wait for the seller to verify your payment.
          </li>

          <li>
            After verification, the seller releases the asset and the
            order is completed automatically.
          </li>
        </ul>
      </section>

      {/* =======================================================
          CHATTING WITH SELLER
      ======================================================== */}

      <section>
        <h3 className="text-lg font-semibold">
          Chatting with the Seller
        </h3>

        <ul className="mt-3 list-disc pl-6 space-y-2 text-sm">
          <li>
            Tap <strong>Chat Seller</strong> on the payment screen to
            message them directly.
          </li>

          <li>
            If you don't already follow the seller, you'll be asked to
            follow them first — this is required before you can open a
            direct message, and also lets you see their future ads.
          </li>

          <li>
            Keep all communication about the trade inside Metisel chat.
          </li>
        </ul>
      </section>

      {/* =======================================================
          VIEWING ORDERS
      ======================================================== */}

      <section>
        <h3 className="text-lg font-semibold">
          Viewing Your Orders
        </h3>

        <ul className="mt-3 list-disc pl-6 space-y-2 text-sm">
          <li>
            Tap <strong>📦 Orders</strong> from the marketplace to see
            all your orders, as both buyer and seller.
          </li>

          <li>
            Each order shows its status — <strong>pending</strong>,
            {" "}<strong>paid</strong>, <strong>completed</strong> or
            {" "}<strong>expired</strong> — along with the amount, order
            ID, date, and remaining time if still active.
          </li>

          <li>
            If you have an active order awaiting payment, a
            <strong> 💳 Go to Payment</strong> shortcut appears at the
            top of the marketplace.
          </li>

          <li>
            As a seller, once a buyer marks an order <strong>paid</strong>,
            a <strong>Confirm Payment</strong> button appears on that
            order — tap it once you've verified the payment yourself.
          </li>
        </ul>
      </section>

      {/* =======================================================
          CANCELLING
      ======================================================== */}

      <section>
        <h3 className="text-lg font-semibold">
          Cancelling an Order
        </h3>

        <ul className="mt-3 list-disc pl-6 space-y-2 text-sm">
          <li>
            Only cancel an order if no payment has been made.
          </li>

          <li>
            If payment has already been sent, do not cancel the order.
          </li>

          <li>
            Orders that are not completed before the payment timer
            expires may automatically expire.
          </li>

          <li>
            Expired orders can no longer be completed.
          </li>
        </ul>
      </section>

      {/* =======================================================
          PAYMENT CONFIRMATION
      ======================================================== */}

      <section>
        <h3 className="text-lg font-semibold">
          Payment Confirmation
        </h3>

        <ul className="mt-3 list-disc pl-6 space-y-2 text-sm">
          <li>
            Buyers should only submit payment proof after payment has
            actually been sent.
          </li>

          <li>
            Upload clear payment proof whenever required.
          </li>

          <li>
            Sellers should verify payment independently before tapping
            <strong> Confirm Payment</strong> in their Orders list or in the chat
          </li>

          <li>
            Once payment is confirmed by the seller, the asset is
            released to the buyer.
          </li>

          <li>
            Never rely solely on screenshots or messages claiming
            payment has been made.
          </li>
        </ul>
      </section>

      {/* =======================================================
          SAFETY
      ======================================================== */}

      <section>
        <h3 className="text-lg font-semibold">
          Safety Tips
        </h3>

        <ul className="mt-3 list-disc pl-6 space-y-2 text-sm">
          <li>
            Always complete trades inside the Metisel P2P Marketplace.
          </li>

          <li>
            Never release digital assets before confirming payment in
            your own financial account.
          </li>

          <li>
            Carefully review payment details before sending funds.
          </li>

          <li>
            Keep all communication within the Metisel chat whenever
            possible.
          </li>

          <li>
            Do not share your passwords, private keys or recovery
            phrases with anyone.
          </li>

          <li>
            Report suspicious users or fraudulent activity immediately.
          </li>

          <li>
            Pay attention to the order countdown timer and complete
            transactions before it expires.
          </li>
        </ul>
      </section>

    </div>
  );
}